import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PlatformFormsModule, SharedUiModule } from '@pf/shared-ui';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectOption } from '@pf/shared-common';
import {
    ZebraBrowserPrintService,
    ZebraPrintZplOptions,
} from '../services/zebra-browser-print.service';

export interface PrintSettings {
    copies: number;
    options: ZebraPrintZplOptions;
}

export interface PrintSettingsChangeEvent extends PrintSettings {
    valid: boolean;
}

@UntilDestroy()
@Component({
    selector: 'platform-print-label-settings',
    standalone: true,
    imports: [PlatformFormsModule, SharedUiModule],
    templateUrl: './print-label-settings.component.html',
    styleUrl: './print-label-settings.component.scss',
})
export class PrintLabelSettingsComponent {
    private readonly zebraBrowserPrintService = inject(
        ZebraBrowserPrintService
    );

    form = new FormGroup({
        copies: new FormControl(1, [Validators.min(1), Validators.max(5)]),
        printer: new FormControl('', [Validators.required]),
        printerSelect: new FormControl<string | null>(null),
    });
    @Input() labelCount = 0;
    @Output() settingsChanged = new EventEmitter<PrintSettingsChangeEvent>();

    availablePrinters: SelectOption[] = [];

    get copies(): number {
        const value = this.form?.value?.copies;
        return typeof value === 'number' && value > 0 ? value : 1;
    }

    get valid(): boolean {
        return this.form.valid;
    }

    get totalLabels(): number {
        return this.labelCount * this.copies;
    }

    constructor() {
        this.zebraBrowserPrintService
            .getAvailablePrinters()
            .pipe(untilDestroyed(this))
            .subscribe(printers => {
                this.availablePrinters = printers.map(p => ({
                    label: p.name,
                    value: p.uid,
                }));
            });

        this.form.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(() => this.onSettingsChanged());
    }

    protected onPrinterSelected(option: SelectOption) {
        if (!option.value) {
            return;
        }
        this.form.get('printer')?.reset();
    }

    protected onPrinterInput(input: string) {
        if (!input) {
            return;
        }
        this.form.get('printerSelect')?.reset();
    }

    protected onSettingsChanged() {
        this.settingsChanged.emit({
            copies: this.copies,
            valid: this.valid,
            options: {
                printerAddress: this.form.value?.printer || '',
                printerUid: this.form.value?.printerSelect || '',
            },
        });
    }
}
